import React, { useEffect, useState } from "react"
import { SubmitHandler, useForm } from "react-hook-form"
import toast from "react-hot-toast"
import { Form } from "reactstrap"
import {
    DropDownList,
    getDistinct,
    getKeys,
    InputLabel,
    merchantSelectors,
    PageContainer,
    PageWrapper,
    StandardButton,
    useSelector,
} from "swiipe.portal.shared"
import SpinnerContainer from "../../components/buttons/SpinnerContainer"
import SubmitButton from "../../components/buttons/SubmitButton"
import HealthStatusSection, { IHealthStatusSectionData } from "../../components/healthStatus/HealthStatusSection"
import WebshopSelect from "../../components/merchant/WebshopSelect"
import PageTitle from "../../components/page/PageTitle"
import { useRequiredUserRelations } from "../../services/userRelationService"
import { StoreState } from "../../store/StoreState"
import { healthStatusSelectors } from "../../store/reducers/healthStatusReducer"
import { userRelationSelectors } from "../../store/reducers/userRelationReducer"
import { getHealthStatusesThunk, updateHealthStatusesThunk } from "../../store/thunks/healthStatusThunks"
import { setSelectedWebshopIdThunk } from "../../store/thunks/merchantThunks"
import useReduxDispatch from "../../store/useReduxDispatch"
import { TProviderName } from "../../type/IProvider"
import { IHealthStatusDto, IHealthStatusSection } from "../../type/healthStatus/IHealthStatusDto"
import { IUserRelationFlattened } from "../../type/user/IUserRelation"
import "./HealthStatusPage.scss"

interface IForm {
    paymentConfigs: {
        [sectionKey: string]: IHealthStatusSectionData
    }
    generalConfig: IHealthStatusSectionData
}

interface IHealthStatusPage {}

const HealthStatusPage = ({}: IHealthStatusPage) => {
    useRequiredUserRelations([{ requiredType: "Merchant" }, { requiredType: "Partner" }])

    const merchantDetails = useSelector(merchantSelectors.merchantDetails)

    const userRelation = useSelector(userRelationSelectors.currentUserRelation)
    const isMasterPartner = userRelation?.isMasterPartner ?? false

    const selectedWebshopId = useSelector(merchantSelectors.selectedWebshopId)

    const healthStatusSections = useSelector((state: StoreState) =>
        healthStatusSelectors.sections(state, isMasterPartner, selectedWebshopId)
    )

    const healthStatuses = useSelector((state: StoreState) =>
        healthStatusSelectors.healthStatuses(state, isMasterPartner, selectedWebshopId)
    )
    const dispatch = useReduxDispatch()
    useEffect(() => {
        if (!userRelation?.isMasterPartner && merchantDetails && !selectedWebshopId) {
            dispatch(setSelectedWebshopIdThunk(merchantDetails?.swMerchant.swMerchantId, merchantDetails?.webshops[0].webshopId))
        }
        if (userRelation?.isMasterPartner || selectedWebshopId) {
            dispatch(getHealthStatusesThunk(false))
        }
    }, [userRelation, selectedWebshopId, merchantDetails])

    if (userRelation?.relationType === "Partner" && !isMasterPartner) {
        return <div>Navigate to a merchant or Aera Support partner to use this page</div>
    }

    const showForm = healthStatuses && userRelation && healthStatusSections

    return (
        <PageWrapper wider>
            <PageContainer id="health-status-page">
                <PageTitle title="Health Status" />
                {merchantDetails && <WebshopSelect merchantDetails={merchantDetails} />}
                <SpinnerContainer showSpinner={!showForm}>
                    {!showForm && <div className="health-status-page__loading-content"></div>}
                    {healthStatuses && userRelation && healthStatusSections && (
                        <HealthStatusPageForm
                            healthStatusSections={healthStatusSections}
                            userRelation={userRelation}
                            healthStatuses={healthStatuses}
                            selectedWebshopId={selectedWebshopId}
                        />
                    )}
                </SpinnerContainer>
            </PageContainer>
        </PageWrapper>
    )
}

interface IHealthStatusPageFormProps {
    userRelation: IUserRelationFlattened
    healthStatuses: IHealthStatusDto[]
    selectedWebshopId?: string
    healthStatusSections: IHealthStatusSection[]
}

const HealthStatusPageForm = ({
    userRelation,
    healthStatuses,
    selectedWebshopId,
    healthStatusSections,
}: IHealthStatusPageFormProps) => {
    const dispatch = useReduxDispatch()

    const { handleSubmit, register, formState, getValues, setValue, reset, errors, watch } = useForm<IForm>()

    const [expandedSections, setExpandedSections] = useState<string[]>([])

    const getSectionKey = (section: IHealthStatusSection): string => section.paymentType + "_" + section.provider
    const getSectionFromKey = (sectionKey: string): IHealthStatusSection => {
        const parts = sectionKey.split("_")
        return healthStatusSections.find((s) => s.paymentType === parts[0] && s.provider === parts[1])!
    }
    const getHealthStatusFromSectionKey = (sectionKey: string | "general") => {
        if (sectionKey === "general") {
            return healthStatuses.find((hs) => !hs.paymentType)
        }
        const section = getSectionFromKey(sectionKey)
        return healthStatuses.find((hs) => hs.paymentType === section.paymentType && hs.provider === section.provider)
    }

    const createNameForInput = (section: IHealthStatusSection | "general", key: string) => {
        return section === "general" ? "generalConfig." + key : "paymentConfigs[" + getSectionKey(section) + "]." + key
    }

    const sectionKeys: (string | "general")[] = ["general", ...healthStatusSections.map((s) => getSectionKey(s))]

    const onSubmit: SubmitHandler<IForm> = async (data) => {
        try {
            const mapToDto = (
                sectionData: IHealthStatusSectionData,
                isGeneral: boolean,
                section: IHealthStatusSection | undefined
            ) => {
                const dto: IHealthStatusDto = {
                    title: sectionData.inheritFromGeneral ? "" : sectionData.title,
                    description: sectionData.inheritFromGeneral ? "" : sectionData.description,
                    enabled: sectionData.inheritFromGeneral ? false : sectionData.enabled,
                    level: sectionData.inheritFromGeneral ? undefined : sectionData.level,
                    overrideGlobal: sectionData.inheritFromGeneral ? false : sectionData.overrideGlobal ?? false,
                    paymentType: isGeneral ? undefined : section?.paymentType,
                    provider: isGeneral ? undefined : section?.provider,
                    inheritFromGeneral: sectionData.inheritFromGeneral ?? false,
                    showFrom: undefined,
                    showTo: undefined,
                    hidePaymentMethod: sectionData.hidePaymentMethod ?? false,
                    hideSavedPaymentMethods: sectionData.hideSavedPaymentMethods ?? false,
                    useFallback: sectionData.useFallback ?? false,
                    updatedByUserId: "", // Will be set by server
                }
                return dto
            }
            const generelDto = mapToDto(data.generalConfig, true, undefined)
            const paymentTypeDtos = Object.keys(data.paymentConfigs).map((sectionKey) =>
                mapToDto(data.paymentConfigs[sectionKey], false, getSectionFromKey(sectionKey))
            )
            await dispatch(updateHealthStatusesThunk([generelDto, ...paymentTypeDtos]))
        } catch (err) {
            // Catch to stop showing spinner
        }
    }

    const expandRelevantSections = () => {
        const values = getValues()
        setExpandedSections(
            sectionKeys.filter((sk) => {
                if (sk === "general") {
                    return true
                }
                const status = values.paymentConfigs[sk]
                return status?.enabled || status?.inheritFromGeneral
            })
        )
    }

    useEffect(() => {
        sectionKeys.forEach((sectionKey) => {
            const healthStatus = getHealthStatusFromSectionKey(sectionKey)
            const data: IHealthStatusSectionData = {
                title: healthStatus?.title ?? "",
                description: healthStatus?.description ?? "",
                level: healthStatus?.level ?? "Low",
                inheritFromGeneral: healthStatus?.inheritFromGeneral ?? false,
                enabled: healthStatus?.enabled ?? false,
                hidePaymentMethod: healthStatus?.hidePaymentMethod ?? false,
                hideSavedPaymentMethods: healthStatus?.hideSavedPaymentMethods ?? false,
                overrideGlobal: healthStatus?.overrideGlobal ?? false,
                useFallback: healthStatus?.useFallback ?? false,
            }
            getKeys(data).forEach((key) =>
                setValue(createNameForInput(sectionKey === "general" ? "general" : getSectionFromKey(sectionKey), key), data[key])
            )
        })
        expandRelevantSections()
    }, [healthStatuses, selectedWebshopId])

    const actionsItems = [
        {
            text: `General - Show default message`,
            value: `general_message`,
        },
        ...getDistinct(healthStatusSections.map((s) => s.provider))
            .map((provider) => {
                return [
                    {
                        text: `${provider} - Hide all with default message`,
                        value: `${provider}_hide_message`,
                    },
                    {
                        text: `${provider} - Hide all`,
                        value: `${provider}_hide`,
                    },
                    {
                        text: `${provider} - Default message`,
                        value: `${provider}_message`,
                    },
                ]
            })
            .flat(),
    ]

    return (
        <Form onSubmit={handleSubmit(onSubmit)}>
            <div className="health-status-page__main-action-container">
                <InputLabel title="Set provider not responding settings">
                    <DropDownList
                        options={actionsItems}
                        placeholder="Choose action"
                        onChange={(e) => {
                            const val = e.currentTarget.value
                            const parts = val.split("_")
                            const shouldHide = parts.includes("hide")
                            const shouldAddMessage = parts.includes("message")
                            const provider: TProviderName | undefined =
                                parts[0] === "general" ? undefined : (parts[0] as TProviderName)
                            e.preventDefault()
                            setValue(createNameForInput("general", "level"), "High")
                            const defaultTitle = "Issues with payments"
                            const defaultDescription =
                                "We are currently experiencing issues with payments.\nWe are looking into it and hope to have it resolved soon."
                            if (!provider && shouldAddMessage) {
                                setValue(createNameForInput("general", "title"), "Issues with payment")
                                setValue(
                                    createNameForInput("general", "description"),
                                    "We are currently experiencing issues with payments.\nWe are looking into it and hope to have it resolved soon."
                                )
                            }
                            setValue(createNameForInput("general", "enabled"), !provider)
                            healthStatusSections
                                .filter((s) => s.provider === provider)
                                .forEach((section) => {
                                    const data: IHealthStatusSectionData = {
                                        title: shouldAddMessage ? defaultTitle : "",
                                        description: shouldAddMessage ? defaultDescription : "",
                                        level: shouldAddMessage ? "High" : "Low",
                                        inheritFromGeneral: false,
                                        enabled: true,
                                        hidePaymentMethod: shouldHide && section.paymentType !== "CreditCard",
                                        hideSavedPaymentMethods: true,
                                        overrideGlobal: false,
                                        useFallback: section.paymentType === "CreditCard" && provider === "PaymentIQ",
                                    }
                                    getKeys(data).forEach((key) => setValue(createNameForInput(section, key), data[key]))
                                })
                            expandRelevantSections()
                            e.target.value = ""
                            toast.success("Action executed: " + (actionsItems.find((ai) => ai.value === val)?.text ?? ""), {
                                position: "top-center",
                            })
                        }}
                    />
                </InputLabel>
                <StandardButton
                    isSmall
                    title="Clear Payment type settings"
                    onClick={(e) => {
                        e.preventDefault()
                        healthStatusSections.forEach((s) => {
                            const data: IHealthStatusSectionData = {
                                title: "",
                                description: "",
                                level: "Low",
                                inheritFromGeneral: false,
                                enabled: false,
                                hidePaymentMethod: false,
                                hideSavedPaymentMethods: false,
                                overrideGlobal: false,
                                useFallback: false,
                            }
                            getKeys(data).forEach((key) => setValue(createNameForInput(s, key), data[key]))
                        })
                        expandRelevantSections()
                    }}
                />
                <SubmitButton isSmall containerClass="btn-save" dark noBorder noBorderRadius formState={formState}>
                    Save changes
                </SubmitButton>
            </div>
            <HealthStatusSection
                register={register}
                data={getHealthStatusFromSectionKey("general")}
                paymentType="general"
                provider="general"
                isGlobal={userRelation.isMasterPartner}
                resolvedShouldInheritFromGeneral={watch(createNameForInput("general", "inheritFromGeneral"))}
                createNameForInput={(key: string) => createNameForInput("general", key)}
                isExpanded={expandedSections.includes("general")}
                expandedDidChange={(isExpanded) => {
                    const withoutSection = expandedSections.filter((es) => es !== "general")
                    setExpandedSections(isExpanded ? [...withoutSection, "general"] : withoutSection)
                }}
            />
            {healthStatusSections.map((s) => {
                const sectionKey = getSectionKey(s)
                return (
                    <div key={sectionKey} className="health-status-page__payment-section-container">
                        <HealthStatusSection
                            paymentType={s.paymentType}
                            provider={s.provider}
                            data={getHealthStatusFromSectionKey(sectionKey)}
                            isGlobal={userRelation.isMasterPartner}
                            register={register}
                            resolvedShouldInheritFromGeneral={watch(createNameForInput(s, "inheritFromGeneral"))}
                            createNameForInput={(key: string) => createNameForInput(s, key)}
                            isExpanded={expandedSections.includes(sectionKey)}
                            expandedDidChange={(isExpanded) => {
                                const withoutSection = expandedSections.filter((es) => es !== sectionKey)
                                setExpandedSections(isExpanded ? [...withoutSection, sectionKey] : withoutSection)
                            }}
                        />
                    </div>
                )
            })}
        </Form>
    )
}

export default HealthStatusPage
